'use client';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Icon from '@/components/ui/AppIcon';
import type { BookingState } from './BookingWizard';

interface Props {
  booking: BookingState;
  updateBooking: (u: Partial<BookingState>) => void;
  onNext: () => void;
  onBack: () => void;
}

interface FormValues {
  firstName: string;
  surname: string;
  dateOfBirth: string;
  idNumber: string;
  address: string;
  mobile: string;
  email: string;
  emergencyContact: string;
  emergencyPhone: string;
  hasMembership: boolean;
  homeClub: string;
  handicap: string;
  cdhNumber: string;
}

function getAgeCategory(dob: string): 'Junior' | 'Adult' | 'Senior' | null {
  if (!dob) return null;
  const birth = new Date(dob);
  const today = new Date(2026, 5, 10);
  const age = today.getFullYear() - birth.getFullYear() - (today < new Date(today.getFullYear(), birth.getMonth(), birth.getDate()) ? 1 : 0);
  if (age < 18) return 'Junior';
  if (age >= 60) return 'Senior';
  return 'Adult';
}

const catStyles: Record<string, string> = {
  Junior: 'badge-junior',
  Adult: 'badge-adult',
  Senior: 'badge-senior',
};

export default function VisitorRegistrationForm({ booking, updateBooking, onNext, onBack }: Props) {
  const { register, handleSubmit, watch, formState: { errors } } = useForm<FormValues>({
    defaultValues: {
      firstName: (booking.visitorData.firstName as string) || '',
      surname: (booking.visitorData.surname as string) || '',
      dateOfBirth: (booking.visitorData.dateOfBirth as string) || '',
      idNumber: (booking.visitorData.idNumber as string) || '',
      address: (booking.visitorData.address as string) || '',
      mobile: (booking.visitorData.mobile as string) || '',
      email: (booking.visitorData.email as string) || '',
      emergencyContact: (booking.visitorData.emergencyContact as string) || '',
      emergencyPhone: (booking.visitorData.emergencyPhone as string) || '',
      hasMembership: false,
      homeClub: (booking.visitorData.homeClub as string) || '',
      handicap: (booking.visitorData.handicap as string) || '',
      cdhNumber: (booking.visitorData.cdhNumber as string) || '',
    },
  });

  const dob = watch('dateOfBirth');
  const hasMembership = watch('hasMembership');
  const [ageCategory, setAgeCategory] = useState<'Junior' | 'Adult' | 'Senior' | null>(null);

  useEffect(() => {
    setAgeCategory(getAgeCategory(dob));
  }, [dob]);

  const onSubmit = (data: FormValues) => {
    // Backend integration point: POST /api/visitors/register
    updateBooking({ visitorData: data as unknown as Record<string, string | boolean> });
    onNext();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          {/* Personal Information */}
          <div className="bg-card border border-border rounded-2xl p-6 shadow-card">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="UserIcon" size={16} variant="outline" className="text-primary" />
              </div>
              <h2 className="text-lg font-bold text-foreground">Personal Information</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">First Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  className={`input-field ${errors.firstName ? 'error' : ''}`}
                  placeholder="e.g. Thabo"
                  {...register('firstName', { required: 'First name is required' })}
                />
                {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Surname <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  className={`input-field ${errors.surname ? 'error' : ''}`}
                  placeholder="e.g. Nkosi"
                  {...register('surname', { required: 'Surname is required' })}
                />
                {errors.surname && <p className="text-red-500 text-xs mt-1">{errors.surname.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Date of Birth <span className="text-red-500">*</span></label>
                <p className="text-xs text-muted-foreground mb-1.5">Used to determine age category (Junior/Adult/Senior)</p>
                <div className="relative">
                  <input
                    type="date"
                    className={`input-field ${errors.dateOfBirth ? 'error' : ''}`}
                    {...register('dateOfBirth', { required: 'Date of birth is required' })}
                  />
                </div>
                {errors.dateOfBirth && <p className="text-red-500 text-xs mt-1">{errors.dateOfBirth.message}</p>}
                {ageCategory && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Age category:</span>
                    <span className={catStyles[ageCategory]}>{ageCategory}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">SA ID Number <span className="text-red-500">*</span></label>
                <p className="text-xs text-muted-foreground mb-1.5">13-digit South African ID number</p>
                <input
                  type="text"
                  className={`input-field ${errors.idNumber ? 'error' : ''}`}
                  placeholder="e.g. 8503125678088"
                  maxLength={13}
                  {...register('idNumber', {
                    required: 'ID number is required',
                    pattern: { value: /^\d{13}$/, message: 'Must be 13 digits' },
                  })}
                />
                {errors.idNumber && <p className="text-red-500 text-xs mt-1">{errors.idNumber.message}</p>}
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-foreground mb-1.5">Residential Address <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  className={`input-field ${errors.address ? 'error' : ''}`}
                  placeholder="e.g. 14 Voortrekker Rd, Pietermaritzburg, 3201"
                  {...register('address', { required: 'Address is required' })}
                />
                {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Mobile Number <span className="text-red-500">*</span></label>
                <input
                  type="tel"
                  className={`input-field ${errors.mobile ? 'error' : ''}`}
                  placeholder="e.g. 082 345 6789"
                  {...register('mobile', {
                    required: 'Mobile number is required',
                    pattern: { value: /^0\d{9}$/, message: 'Enter a valid 10-digit SA mobile number' },
                  })}
                />
                {errors.mobile && <p className="text-red-500 text-xs mt-1">{errors.mobile.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Email Address <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  className={`input-field ${errors.email ? 'error' : ''}`}
                  placeholder="e.g. thabo@gmail.com"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                  })}
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Emergency Contact Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  className={`input-field ${errors.emergencyContact ? 'error' : ''}`}
                  placeholder="Full name of emergency contact"
                  {...register('emergencyContact', { required: 'Emergency contact name is required' })}
                />
                {errors.emergencyContact && <p className="text-red-500 text-xs mt-1">{errors.emergencyContact.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Emergency Contact Number <span className="text-red-500">*</span></label>
                <input
                  type="tel"
                  className={`input-field ${errors.emergencyPhone ? 'error' : ''}`}
                  placeholder="e.g. 073 111 2233"
                  {...register('emergencyPhone', { required: 'Emergency contact number is required' })}
                />
                {errors.emergencyPhone && <p className="text-red-500 text-xs mt-1">{errors.emergencyPhone.message}</p>}
              </div>
            </div>
          </div>

          {/* Golf Information */}
          <div className="bg-card border border-border rounded-2xl p-6 shadow-card">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
                <span className="text-base">⛳</span>
              </div>
              <h2 className="text-lg font-bold text-foreground">Golf Information</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-foreground mb-3">Current Golf Club Membership</label>
                <div className="flex gap-3">
                  {[
                    { value: true, label: 'Yes, I am a club member' },
                    { value: false, label: 'No, I am not a member' },
                  ].map((opt) => (
                    <label
                      key={`membership-${String(opt.value)}`}
                      className={`flex-1 flex items-center gap-3 p-3.5 rounded-xl border-2 cursor-pointer transition-all duration-150 ${
                        hasMembership === opt.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'
                      }`}
                    >
                      <input
                        type="radio"
                        value={String(opt.value)}
                        className="sr-only"
                        {...register('hasMembership')}
                        onChange={() => {}}
                        checked={hasMembership === opt.value}
                      />
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${hasMembership === opt.value ? 'border-primary' : 'border-border'}`}>
                        {hasMembership === opt.value && <div className="w-2 h-2 rounded-full bg-primary" />}
                      </div>
                      <span className="text-sm font-medium text-foreground">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {hasMembership && (
                <div className="sm:col-span-2">
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Home Club Name</label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="e.g. Royal Durban Golf Club"
                    {...register('homeClub')}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Handicap Index</label>
                <p className="text-xs text-muted-foreground mb-1.5">Enter 0 for scratch, leave blank if unregistered</p>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="54"
                  className="input-field"
                  placeholder="e.g. 14.2"
                  {...register('handicap')}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">CDH Number</label>
                <p className="text-xs text-muted-foreground mb-1.5">Central Database of Handicaps (if registered)</p>
                <input
                  type="text"
                  className="input-field"
                  placeholder="e.g. CDH-29341"
                  {...register('cdhNumber')}
                />
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onBack}
              className="flex-1 py-3 rounded-xl border-2 border-border text-foreground font-semibold text-sm hover:bg-muted transition-colors flex items-center justify-center gap-2"
            >
              <Icon name="ChevronLeftIcon" size={16} variant="outline" />
              Back
            </button>
            <button
              type="submit"
              className="flex-1 btn-primary py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
            >
              Continue to Consent
              <Icon name="ArrowRightIcon" size={16} variant="outline" />
            </button>
          </div>
        </div>

        {/* Right Info Panel */}
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-2xl p-5 shadow-card">
            <h3 className="font-semibold text-foreground text-sm mb-3 flex items-center gap-2">
              <Icon name="ShieldCheckIcon" size={16} variant="outline" className="text-primary" />
              Why We Collect This
            </h3>
            <ul className="space-y-2.5 text-sm text-muted-foreground">
              {[
                'Your details are securely stored and never shared with third parties.',
                'Age category determines applicable green fee pricing.',
                'CDH number enables official handicap verification.',
                'Emergency contact is required for on-course safety.',
              ].map((item, i) => (
                <li key={`why-${i}`} className="flex items-start gap-2">
                  <Icon name="CheckCircleIcon" size={14} variant="outline" className="text-secondary shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-amber-light border border-amber-200 rounded-2xl p-4">
            <div className="flex items-start gap-2">
              <Icon name="InformationCircleIcon" size={16} variant="outline" className="text-amber-light-foreground shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-light-foreground">Returning Visitor?</p>
                <p className="text-xs text-amber-light-foreground/80 mt-1">
                  If you have played at MGC before, your details may already be on file.{' '}
                  <a href="/visitor-registration-sign-up-login" className="underline font-medium">Sign in</a>{' '}
                  to auto-fill your information.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-2xl p-5 shadow-card">
            <h3 className="font-semibold text-foreground text-sm mb-2">Booking Summary</h3>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between text-muted-foreground">
                <span>Date</span>
                <span className="text-foreground font-medium">
                  {booking.selectedDate ? new Date(booking.selectedDate + 'T12:00:00').toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' }) : '—'}
                </span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Tee Time</span>
                <span className="text-foreground font-medium font-tabular">{booking.selectedTime || '—'}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Players</span>
                <span className="text-foreground font-medium">{booking.players}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Holes</span>
                <span className="text-foreground font-medium">{booking.holes}</span>
              </div>
              <div className="flex justify-between font-bold text-foreground border-t border-border pt-2 mt-2">
                <span>Total</span>
                <span className="font-tabular text-primary">R{booking.total}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}